import { useState } from 'react';
import { getUserErrorMessage } from '../utils/errorMessages';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, CheckCircle, Lock, Eye, EyeOff } from 'lucide-react';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Wachtwoorden komen niet overeen');
      return;
    }

    if (password.length < 8) {
      toast.error('Wachtwoord moet minimaal 8 tekens bevatten');
      return;
    }

    setLoading(true);

    try {
      await resetPassword(token, password);
      setSuccess(true);
      toast.success('Wachtwoord succesvol gewijzigd');
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Het wachtwoord kon niet worden hersteld. Controleer de link en probeer het opnieuw.'));
    } finally {
      setLoading(false);
    }
  };

  // Invalid/missing token state
  if (!token) {
    return (
      <div className="min-h-screen w-full relative overflow-hidden">
        {/* Animated Gradient Background */}
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, #ff9a56 0%, #ff6b8a 25%, #c44eff 50%, #7b68ee 75%, #00bfff 100%)`,
              backgroundSize: '400% 400%',
              animation: 'gradientShift 15s ease infinite'
            }}
          />
          <svg 
            className="absolute bottom-0 right-0 w-full h-full opacity-30"
            viewBox="0 0 1440 900" 
            preserveAspectRatio="xMaxYMax slice"
          >
            <path fill="url(#wave1)" d="M0,900 C300,700 600,800 900,600 C1200,400 1440,500 1440,0 L1440,900 Z" />
            <path fill="url(#wave2)" d="M0,900 C200,750 500,850 800,650 C1100,450 1440,550 1440,100 L1440,900 Z" opacity="0.7" />
            <defs>
              <linearGradient id="wave1" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#ff6b8a" />
                <stop offset="100%" stopColor="#c44eff" />
              </linearGradient>
              <linearGradient id="wave2" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#ffd194" />
                <stop offset="100%" stopColor="#ff6b8a" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        <header className="relative z-10 p-6">
          <Link to="/" className="text-2xl font-bold tracking-tight text-black hover:opacity-80 transition-opacity">
            Spooker
          </Link>
        </header>

        <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-120px)] px-4">
          <div className="w-full max-w-md">
            <div className="bg-white rounded-xl shadow-2xl overflow-hidden p-8 text-center">
              <div className="mx-auto h-16 w-16 rounded-full bg-red-50 flex items-center justify-center mb-6">
                <Lock className="h-8 w-8 text-red-600" strokeWidth={1.5} />
              </div>
              <h1 className="text-2xl font-semibold text-neutral-900 mb-2">
                Ongeldige link
              </h1>
              <p className="text-sm text-neutral-600 mb-6">
                Deze reset-link is ongeldig of verlopen. Vraag een nieuwe aan.
              </p>
              <Link to="/forgot-password">
                <Button className="w-full h-12 bg-[#635bff] hover:bg-[#4b45c6] text-white font-medium rounded-lg">
                  Nieuwe link aanvragen
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <footer className="relative z-10 p-6 flex items-center justify-between text-sm text-neutral-600">
          <span>© 2026 Spooker</span>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-neutral-900">Privacy</Link>
            <Link to="/terms" className="hover:text-neutral-900">Voorwaarden</Link>
          </div>
        </footer>

        <style>{`
          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full relative overflow-hidden">
      {/* Animated Gradient Background */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, #ff9a56 0%, #ff6b8a 25%, #c44eff 50%, #7b68ee 75%, #00bfff 100%)`,
            backgroundSize: '400% 400%',
            animation: 'gradientShift 15s ease infinite'
          }}
        />
        <svg 
          className="absolute bottom-0 right-0 w-full h-full opacity-30"
          viewBox="0 0 1440 900" 
          preserveAspectRatio="xMaxYMax slice"
        >
          <path fill="url(#wave1)" d="M0,900 C300,700 600,800 900,600 C1200,400 1440,500 1440,0 L1440,900 Z" />
          <path fill="url(#wave2)" d="M0,900 C200,750 500,850 800,650 C1100,450 1440,550 1440,100 L1440,900 Z" opacity="0.7" />
          <defs>
            <linearGradient id="wave1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ff6b8a" />
              <stop offset="100%" stopColor="#c44eff" />
            </linearGradient>
            <linearGradient id="wave2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ffd194" />
              <stop offset="100%" stopColor="#ff6b8a" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Header */}
      <header className="relative z-10 p-6">
        <Link to="/" className="text-2xl font-bold tracking-tight text-black hover:opacity-80 transition-opacity">
          Spooker
        </Link>
      </header>

      {/* Card */}
      <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-120px)] px-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
            <div className="p-8">
              {!success ? (
                <>
                  {/* Back Link */}
                  <Link
                    to="/login"
                    className="inline-flex items-center text-sm text-neutral-500 hover:text-neutral-700 transition-colors mb-6"
                  >
                    <ArrowLeft className="mr-1 h-4 w-4" strokeWidth={1.5} />
                    Terug naar inloggen
                  </Link>

                  <h1 className="text-2xl font-semibold text-neutral-900 mb-2">
                    Nieuw wachtwoord instellen
                  </h1>
                  <p className="text-sm text-neutral-600 mb-6">
                    Kies een sterk wachtwoord van minimaal 8 tekens.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-sm font-medium text-neutral-700">
                        Nieuw wachtwoord
                      </Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Minimaal 8 tekens"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          minLength={8}
                          className="h-12 border-2 border-neutral-200 focus:border-[#635bff] focus:ring-[#635bff] rounded-lg pr-10"
                          data-testid="reset-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                        >
                          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-sm font-medium text-neutral-700">
                        Bevestig wachtwoord
                      </Label>
                      <div className="relative">
                        <Input
                          id="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="Herhaal uw wachtwoord"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          className="h-12 border-2 border-neutral-200 focus:border-[#635bff] focus:ring-[#635bff] rounded-lg pr-10"
                          data-testid="reset-confirm-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                        >
                          {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full h-12 bg-[#635bff] hover:bg-[#4b45c6] text-white font-medium rounded-lg transition-colors"
                      disabled={loading}
                      data-testid="reset-password-submit-button"
                    >
                      {loading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        'Wachtwoord wijzigen'
                      )}
                    </Button>
                  </form>
                </>
              ) : (
                /* Success State */
                <div className="text-center py-4">
                  <div className="mx-auto h-16 w-16 rounded-full bg-green-50 flex items-center justify-center mb-6">
                    <CheckCircle className="h-8 w-8 text-green-600" strokeWidth={1.5} />
                  </div>
                  <h1 className="text-2xl font-semibold text-neutral-900 mb-2">
                    Wachtwoord gewijzigd
                  </h1>
                  <p className="text-sm text-neutral-600 mb-6">
                    U kunt nu inloggen met uw nieuwe wachtwoord.
                  </p>
                  <Button
                    onClick={() => navigate('/login')}
                    className="w-full h-12 bg-[#635bff] hover:bg-[#4b45c6] text-white font-medium rounded-lg transition-colors"
                    data-testid="go-to-login-button"
                  >
                    Ga naar inloggen
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 p-6 flex items-center justify-between text-sm text-neutral-600">
        <span>© 2026 Spooker</span>
        <div className="flex gap-4">
          <Link to="/privacy" className="hover:text-neutral-900">Privacy</Link>
          <Link to="/terms" className="hover:text-neutral-900">Voorwaarden</Link>
        </div>
      </footer>

      {/* CSS Animation */}
      <style>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </div>
  );
};

export default ResetPasswordPage;
